import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Radio, ShieldCheck, Lock, Loader2, KeyRound, Save, RefreshCw, Unplug,
  Copy, CheckCircle2, ExternalLink, Building2,
} from "lucide-react";

const STEPS = [
  "Create a Business app at developers.facebook.com › My Apps › Create App.",
  "Add the product “Facebook Login for Business”, then paste the Redirect URI below into Valid OAuth Redirect URIs.",
  "Copy your App ID and App Secret from Settings › Basic and save them here.",
  "While the app is in Development mode, only people with an app role (admin/developer/tester) can connect. Submit for App Review to onboard outside clients.",
];

export default function MetaIntegration() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [creds, setCreds] = useState({ app_id: "", app_secret: "", config_id: "" });
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(async () => {
    const res = await api.get("/meta/status");
    setStatus(res.data);
    setLoading(false);
    if (res.data.connected) {
      try {
        const acc = await api.get("/meta/adaccounts");
        setAccounts(acc.data.accounts || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load ad accounts",
          { id: "meta-adaccounts-error" });
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const flag = params.get("meta");
    if (!flag) return;
    if (flag === "connected") toast.success("Meta account connected — pick an ad account to go live");
    else toast.error(params.get("reason") || "Meta connection failed");
    params.delete("meta"); params.delete("reason");
    setParams(params, { replace: true });
    // eslint-disable-next-line
  }, []);

  const saveCreds = async () => {
    setBusy("creds");
    try {
      await api.post("/meta/app-config", creds);
      toast.success("Meta app credentials saved securely");
      setCreds({ app_id: "", app_secret: "", config_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save credentials");
    } finally { setBusy(""); }
  };

  const connect = () => { window.location.href = `${API}/meta/oauth/start`; };

  const selectAccount = async (id) => {
    setBusy("select");
    try {
      const res = await api.post("/meta/select-account", { ad_account_id: id });
      const c = res.data.synced;
      toast.success(`Live: ${c.campaigns} campaigns · ${c.ads} ads · ${c.insights} daily rows`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not select account");
    } finally { setBusy(""); }
  };

  const sync = async (days = 90) => {
    setBusy("sync");
    try {
      const res = await api.post(`/meta/sync?days=${days}`);
      const c = res.data.synced;
      toast.success(`Synced ${c.insights} daily rows across ${c.campaigns} campaigns`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setBusy(""); }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await api.post("/meta/disconnect");
      setAccounts([]);
      toast.success("Disconnected — live data removed");
      load();
    } finally { setBusy(""); }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(status.redirect_uri);
    toast.success("Redirect URI copied");
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 text-lime-400 animate-spin" /></div>;

  const live = status.connected && status.ad_account_id;

  return (
    <div className="space-y-6 animate-fade-up max-w-5xl">
      {/* Status banner */}
      <div className={`relative overflow-hidden rounded-xl border p-4 sm:p-6 ${live
        ? "border-lime-400/30 bg-gradient-to-br from-lime-400/10 to-[#14181B]"
        : "border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-[#14181B]"}`}>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 border ${live
            ? "bg-lime-400/15 border-lime-400/40" : "bg-amber-500/15 border-amber-500/40"}`}>
            <Radio className={`h-6 w-6 ${live ? "text-lime-400" : "text-amber-400"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full animate-pulse-dot ${live ? "bg-lime-400" : "bg-amber-400"}`} />
              <span data-testid="meta-status" className={`font-mono text-sm font-bold uppercase tracking-[0.16em] ${live ? "text-lime-400" : "text-amber-400"}`}>
                {live ? "Live · Meta connected" : status.connected ? "Connected · select ad account" : "Not connected"}
              </span>
            </div>
            <p className="mt-2 text-zinc-300">
              {live
                ? `${status.ad_account_name} (${status.ad_account_id}) · ${status.currency || ""}`
                : status.app_configured
                  ? "Your Meta app is configured. Connect an account to pull live campaigns and insights."
                  : "Add your Meta app credentials below, then clients can connect their own ad accounts."}
            </p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono text-zinc-500">
              <span>graph {status.graph_version}</span>
              {status.last_sync_at && <span data-testid="meta-last-sync">last sync {new Date(status.last_sync_at).toLocaleString()}</span>}
              {status.token_expires_at && <span>token expires {new Date(status.token_expires_at).toLocaleDateString()}</span>}
              {status.granted_scopes?.length > 0 && <span>{status.granted_scopes.join(" · ")}</span>}
            </div>
            {status.last_error && <p className="mt-2 text-xs text-rose-400 font-mono">{status.last_error}</p>}
          </div>
          {status.connected && (
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0 w-full sm:w-auto">
              <button data-testid="meta-sync-btn" onClick={() => sync(90)} disabled={!!busy}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 disabled:opacity-60 px-4 min-h-[44px] text-sm font-semibold text-[#0A0D0E]">
                {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now
              </button>
              <button data-testid="meta-disconnect-btn" onClick={disconnect} disabled={!!busy}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#242B30] text-zinc-400 hover:text-rose-400 px-4 min-h-[44px] text-sm">
                <Unplug className="h-4 w-4" /> Disconnect
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Setup steps */}
        <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-lime-400" />
            <h3 className="font-display font-semibold text-white">One-time Meta app setup</h3>
          </div>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-400">
                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-lime-400/10 border border-lime-400/30 text-lime-400 text-[10px] font-mono flex items-center justify-center">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>

          <div className="mt-5">
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Valid OAuth Redirect URI</Label>
            <div className="mt-1.5 flex items-start gap-2 rounded-lg bg-[#0F1315] border border-[#242B30] px-3 py-2.5">
              <code data-testid="meta-redirect-uri" className="text-[11px] sm:text-xs text-lime-400 font-mono break-all flex-1 min-w-0">{status.redirect_uri}</code>
              <button data-testid="copy-redirect-btn" onClick={copyRedirect} className="p-2.5 -m-1.5 text-zinc-500 hover:text-lime-400 shrink-0">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer"
            data-testid="meta-devportal-link"
            className="mt-4 inline-flex items-center gap-2 py-2 text-sm text-lime-400 hover:text-lime-300">
            Open Meta for Developers <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* Credentials + connect */}
        <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-4 w-4 text-lime-400" />
            <h3 className="font-display font-semibold text-white">App credentials</h3>
            {status.app_configured && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase text-lime-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> saved
              </span>
            )}
          </div>

          {status.app_configured && (
            <div className="mb-4 rounded-lg bg-[#0F1315] border border-[#242B30] px-3 py-2.5 text-xs font-mono text-zinc-400 break-words">
              App ID <span className="text-white">{status.app_id}</span> · secret stored encrypted server-side
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Meta App ID</Label>
              <Input data-testid="meta-app-id" value={creds.app_id} placeholder="1234567890123456"
                onChange={(e) => setCreds({ ...creds, app_id: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Meta App Secret</Label>
              <Input data-testid="meta-app-secret" type="password" value={creds.app_secret} placeholder="••••••••••••••••"
                onChange={(e) => setCreds({ ...creds, app_secret: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Login Configuration ID (optional)</Label>
              <Input data-testid="meta-config-id" value={creds.config_id} placeholder="Login for Business config ID"
                onChange={(e) => setCreds({ ...creds, config_id: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
            </div>
          </div>

          <button data-testid="save-meta-creds" onClick={saveCreds} disabled={!creds.app_id || !creds.app_secret || !!busy}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-lime-400/40 text-lime-400 hover:bg-lime-400/10 disabled:opacity-50 px-5 py-2.5 text-sm font-semibold">
            {busy === "creds" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save credentials
          </button>

          <button data-testid="meta-connect-btn" onClick={connect} disabled={!status.app_configured}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1877F2] hover:bg-[#1668d8] disabled:opacity-40 px-5 py-3 text-sm font-semibold text-white">
            <Radio className="h-4 w-4" /> {status.connected ? "Reconnect with Facebook" : "Connect with Facebook"}
          </button>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#0F1315] border border-[#242B30] p-3">
            <Lock className="h-4 w-4 text-lime-400 shrink-0 mt-0.5" />
            <p className="text-xs text-zinc-400">App secret and access tokens are encrypted and stored <span className="text-zinc-200">server-side only</span>. Requested permissions: {status.scopes_requested?.join(", ")}.</p>
          </div>
        </div>
      </div>

      {/* Ad accounts */}
      {status.connected && (
        <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-lime-400" />
            <h3 className="font-display font-semibold text-white">Ad accounts on this login</h3>
          </div>
          {accounts.length === 0 ? (
            <p className="text-sm text-zinc-500 font-mono">No ad accounts returned for this Meta user.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {accounts.map((a) => {
                const active = a.id === status.ad_account_id;
                return (
                  <button key={a.id} data-testid={`ad-account-${a.account_id}`} disabled={!!busy}
                    onClick={() => selectAccount(a.id)}
                    className={`text-left rounded-lg border px-4 py-3 transition-colors ${active
                      ? "border-lime-400/50 bg-lime-400/5" : "border-[#242B30] bg-[#0F1315] hover:border-lime-400/30"}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white truncate">{a.name}</span>
                      {active && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-lime-400/15 text-lime-400 border border-lime-400/30">live</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono mt-1">{a.id} · {a.currency} {a.business ? `· ${a.business}` : ""}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
